import { TabletButton } from '@campfire/tablet-button';
import { Box, Checkbox, DialogActions, DialogContent, Typography } from '@material-ui/core';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { getAlertCardColors } from '../../../../common/cards/alert-card/AlertCard';
import { FullscreenDialog } from '../../../../common/dialogs/FullscreenDialog';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';

interface Session {
  sessionId: string;
  name: string;
  startTime: string | null;
  endTime: string | null;
  maxVolunteers?: number | null;
  currentCount?: number;
}

interface EventDate {
  date: string;
  sessions: Session[];
}

interface EventSessionPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selections: Array<{ date: string; sessionIds: string[]; backupSessionIds: string[] }>) => void;
  eventDates: EventDate[];
  activityName: string;
  hasOpenRoster: boolean;
  isLoading?: boolean;
  isApplicant?: boolean;
  showCapacity?: boolean;
  initialSelections?: Record<string, string[]>;
  maxSessions?: number | null;
  minSessions?: number | null;
  allowBackupSessions?: boolean;
}

const formatTime = (time: string | null): string => {
  if (!time) return '';
  try {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'pm' : 'am';
    const displayHour = h % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  } catch (_unusedErr) {
    return time;
  }
};

const formatDate = (dateStr: string): { day: string; monthYear: string; weekday: string } => {
  const dt = DateTime.fromISO(dateStr);
  return {
    day: dt.toFormat('d'),
    monthYear: dt.toFormat('MMM yyyy'),
    weekday: dt.toFormat('cccc'),
  };
};

export const EventSessionPickerDialog = ({
  open,
  onClose,
  onConfirm,
  eventDates,
  activityName,
  hasOpenRoster,
  isLoading,
  isApplicant,
  showCapacity = false,
  initialSelections,
  maxSessions,
  minSessions,
  allowBackupSessions = false,
}: EventSessionPickerDialogProps) => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>(() =>
    Object.entries(initialSelections ?? {}).flatMap(([date, ids]) => ids.map((id) => `${date}|${id}`))
  );

  const selections = React.useMemo(() => {
    const grouped: Record<string, string[]> = {};
    selectedKeys.forEach((key) => {
      const separator = key.lastIndexOf('|');
      const date = key.slice(0, separator);
      const sessionId = key.slice(separator + 1);
      grouped[date] = [...(grouped[date] ?? []), sessionId];
    });
    return grouped;
  }, [selectedKeys]);
  const { isMobile, theme } = useCampfireTheme();
  const infoColors = getAlertCardColors('info');
  const successColors = getAlertCardColors('success');

  const sessionLimit = hasOpenRoster && maxSessions != null ? maxSessions : null;

  const capSelections = sessionLimit !== null && !allowBackupSessions;

  const fullKeys = React.useMemo(() => {
    const set = new Set<string>();
    if (!showCapacity) return set;
    eventDates.forEach((eventDate) => {
      eventDate.sessions.forEach((session) => {
        if (
          session.maxVolunteers != null &&
          session.currentCount != null &&
          session.currentCount >= session.maxVolunteers
        ) {
          set.add(`${eventDate.date}|${session.sessionId}`);
        }
      });
    });
    return set;
  }, [eventDates, showCapacity]);

  const toggleSession = (date: string, sessionId: string) => {
    const key = `${date}|${sessionId}`;
    setSelectedKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((existing) => existing !== key);
      }

      if (sessionLimit !== null && !allowBackupSessions && prev.length >= sessionLimit) {
        return prev;
      }

      return [...prev, key];
    });
  };

  const toggleDate = (date: string, sessionIds: string[]) => {
    const keys = sessionIds.map((id) => `${date}|${id}`);
    setSelectedKeys((prev) => {
      const allSelected = keys.every((key) => prev.includes(key));
      if (allSelected) {
        return prev.filter((existing) => !keys.includes(existing));
      }

      const toAdd = keys.filter((key) => !prev.includes(key));
      if (sessionLimit === null || allowBackupSessions) {
        return [...prev, ...toAdd];
      }

      const availableSlots = Math.max(0, sessionLimit - prev.length);
      return [...prev, ...toAdd.slice(0, availableSlots)];
    });
  };

  const totalSelected = selectedKeys.length;

  const rosteredKeys = React.useMemo(() => {
    const primaryCandidates = selectedKeys.filter((key) => !fullKeys.has(key));
    if (sessionLimit === null) return new Set(primaryCandidates);
    return new Set(primaryCandidates.slice(0, sessionLimit));
  }, [selectedKeys, sessionLimit, fullKeys]);

  const isBackupKey = React.useCallback((key: string) => fullKeys.has(key) || !rosteredKeys.has(key), [
    fullKeys,
    rosteredKeys,
  ]);

  const limitReached = sessionLimit !== null && rosteredKeys.size >= sessionLimit;

  const selectionOrder = React.useMemo(() => {
    const order = new Map<string, number>();
    selectedKeys.forEach((key, index) => order.set(key, index + 1));
    return order;
  }, [selectedKeys]);

  const backupCount = selectedKeys.filter((key) => isBackupKey(key)).length;
  const rosteredCount = totalSelected - backupCount;

  const showSelectionOrder = sessionLimit !== null || (isApplicant && minSessions != null && minSessions > 0);

  const sessionMinimum = isApplicant && minSessions != null && minSessions > 0 ? minSessions : null;
  const minimumMet = sessionMinimum === null || totalSelected >= sessionMinimum;
  const sessionsShort = sessionMinimum === null ? 0 : Math.max(0, sessionMinimum - totalSelected);
  const datesSelected = Object.entries(selections).filter(([_unusedKey, ids]) => ids.length > 0).length;

  const handleConfirm = () => {
    const result = Object.entries(selections)
      .filter(([_unusedKey, sessionIds]) => sessionIds.length > 0)
      .map(([date, sessionIds]) => ({
        date,
        sessionIds,
        backupSessionIds: sessionIds.filter((sessionId) => isBackupKey(`${date}|${sessionId}`)),
      }));
    onConfirm(result);
  };

  return (
    <FullscreenDialog
      open={open}
      close={onClose}
      fullScreen={isMobile}
      title='Select Your Dates & Sessions'
      scroll='body'
      maxWidth='md'
    >
      <DialogContent style={{ minWidth: isMobile ? 290 : 760, paddingTop: 24 }}>
        <Typography style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>{activityName}</Typography>
        <Typography variant='body2' color='textSecondary' style={{ marginBottom: 20 }}>
          {isApplicant
            ? 'Select the dates and sessions you would like to attend. Your spot will be held while you complete your profile.'
            : hasOpenRoster
            ? 'Select the dates and sessions you want to attend. You will be rostered immediately.'
            : 'Select the dates and sessions you are available for. The manager will finalise the roster.'}
        </Typography>

        {(sessionLimit !== null || sessionMinimum !== null) &&
          (() => {
            const showAsComplete = minimumMet && (sessionLimit === null || limitReached);
            const calloutColors = showAsComplete ? successColors : infoColors;
            const CalloutIcon = calloutColors.rightIcon;

            let calloutText;
            if (!minimumMet) {
              calloutText = `Select at least ${sessionMinimum} session${
                sessionMinimum === 1 ? '' : 's'
              } to continue (${totalSelected} selected)`;
            } else if (limitReached) {
              calloutText = `${rosteredCount} session${rosteredCount === 1 ? '' : 's'} rostered${
                backupCount > 0 ? `, ${backupCount} backup` : ''
              }`;
            } else if (sessionLimit !== null) {
              calloutText = `Select up to ${sessionLimit} session${
                sessionLimit === 1 ? '' : 's'
              } (${totalSelected} selected)`;
            } else {
              calloutText = `Minimum of ${sessionMinimum} session${
                sessionMinimum === 1 ? '' : 's'
              } selected (${totalSelected} selected)`;
            }

            return (
              <Box
                display='flex'
                alignItems='center'
                bgcolor={calloutColors.backgroundColor}
                marginBottom='20px'
                style={{ borderRadius: 6, padding: '12px 14px' }}
              >
                <CalloutIcon style={{ fontSize: 18, marginRight: 10, color: calloutColors.iconColor }} />
                <Typography variant='subtitle2' style={{ fontSize: 13, color: calloutColors.darkTextColor }}>
                  {calloutText}
                  {allowBackupSessions && minimumMet && !limitReached && sessionLimit !== null
                    ? '. Anything beyond that is saved as backup availability.'
                    : ''}
                </Typography>
              </Box>
            );
          })()}

        <Box display='flex' overflowX='auto' overflowY='hidden' pb={2} style={{ gap: 16 }}>
          {[...eventDates]
            .sort((a, b) => a.date.localeCompare(b.date))
            .map((eventDate) => {
              const { day, monthYear, weekday } = formatDate(eventDate.date);
              const dateSessionIds = eventDate.sessions
                .filter(
                  (s) =>
                    !showCapacity ||
                    !(s.maxVolunteers != null && s.currentCount != null && s.currentCount >= s.maxVolunteers)
                )
                .map((s) => s.sessionId);
              const selectedForDate = selections[eventDate.date] ?? [];

              const hasVisibleSession = eventDate.sessions.some(
                (s) => !(fullKeys.has(`${eventDate.date}|${s.sessionId}`) && !allowBackupSessions)
              );
              if (!hasVisibleSession) return null;

              return (
                <Box
                  key={eventDate.date}
                  minWidth={isMobile ? 200 : 260}
                  borderRadius={12}
                  border={`1px solid ${
                    selectedForDate.length > 0 ? theme.palette.primary.main : theme.color.grey.border
                  }`}
                  bgcolor={selectedForDate.length > 0 ? 'rgba(121, 111, 255, 0.03)' : '#fff'}
                  overflow='hidden'
                  flexShrink={0}
                  style={{ transition: 'border-color 0.15s' }}
                >
                  <Box
                    px={2}
                    py={1.5}
                    bgcolor={selectedForDate.length > 0 ? theme.palette.primary.main : theme.color.grey.neutralBrand100}
                    style={{ cursor: 'pointer', transition: 'background-color 0.15s' }}
                    onClick={() => toggleDate(eventDate.date, dateSessionIds)}
                  >
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                      <Box>
                        <Typography
                          style={{
                            fontWeight: 800,
                            fontSize: 22,
                            color: selectedForDate.length > 0 ? '#fff' : theme.color.grey.neutral400,
                            lineHeight: 1.1,
                          }}
                        >
                          {day}
                        </Typography>
                        <Typography
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: selectedForDate.length > 0 ? 'rgba(255,255,255,0.8)' : theme.color.grey.neutral200,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                          }}
                        >
                          {monthYear}
                        </Typography>
                      </Box>
                      <Typography
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: selectedForDate.length > 0 ? 'rgba(255,255,255,0.7)' : theme.color.grey.neutral200,
                        }}
                      >
                        {weekday}
                      </Typography>
                    </Box>
                  </Box>

                  <Box px={1} py={1}>
                    {eventDate.sessions.map((session) => {
                      const sessionKey = `${eventDate.date}|${session.sessionId}`;
                      const isCommittedFull = fullKeys.has(sessionKey);

                      if (isCommittedFull && !allowBackupSessions) return null;

                      const isSelected = selectedForDate.includes(session.sessionId);
                      const isBlockedByLimit = !isSelected && limitReached && capSelections;
                      const isBackup = isSelected && isBackupKey(sessionKey);
                      const selectionPosition = selectionOrder.get(sessionKey);
                      const timeLabel =
                        session.startTime && session.endTime
                          ? `${formatTime(session.startTime)} – ${formatTime(session.endTime)}`
                          : null;
                      const hasCapacityData =
                        showCapacity && session.maxVolunteers != null && session.currentCount != null;
                      const fillPercent = showCapacity
                        ? Math.round(((session.currentCount ?? 0) / (session.maxVolunteers ?? 1)) * 100)
                        : 0;

                      return (
                        <Box
                          key={session.sessionId}
                          display='flex'
                          alignItems='flex-start'
                          px={1}
                          py={0.75}
                          borderRadius={6}
                          style={{
                            cursor: isBlockedByLimit ? 'not-allowed' : 'pointer',
                            opacity: isBlockedByLimit ? 0.45 : 1,
                          }}
                          bgcolor={isSelected && !isBackup ? 'rgba(121, 111, 255, 0.06)' : 'transparent'}
                          onClick={() => {
                            if (isBlockedByLimit) return;
                            toggleSession(eventDate.date, session.sessionId);
                          }}
                        >
                          {showSelectionOrder ? (
                            <Box
                              display='flex'
                              alignItems='center'
                              justifyContent='center'
                              flexShrink={0}
                              style={{
                                width: 18,
                                height: 18,
                                margin: 6,
                                borderRadius: 3,
                                border: `2px solid ${
                                  isSelected && !isBackup ? theme.palette.primary.main : theme.color.grey.border
                                }`,
                                backgroundColor: isSelected && !isBackup ? theme.palette.primary.main : 'transparent',
                              }}
                            >
                              <Typography
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  lineHeight: 1,
                                  color: isSelected && !isBackup ? '#fff' : theme.color.grey.neutral300,
                                }}
                              >
                                {selectionPosition ?? ''}
                              </Typography>
                            </Box>
                          ) : (
                            <Checkbox
                              checked={isSelected}
                              disabled={isBlockedByLimit}
                              color='primary'
                              size='small'
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleSession(eventDate.date, session.sessionId)}
                              style={{ padding: 4 }}
                            />
                          )}
                          <Box ml={0.5} flex={1}>
                            <Box display='flex' alignItems='center' style={{ gap: 6 }}>
                              <Typography style={{ fontSize: 13, fontWeight: 600, color: '#333', lineHeight: 1.3 }}>
                                {session.name}
                              </Typography>
                              {isCommittedFull && (
                                <Typography
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    textTransform: 'uppercase',
                                    color: '#B26A00',
                                    backgroundColor: 'rgba(240, 160, 80, 0.16)',
                                    borderRadius: 3,
                                    padding: '0 4px',
                                  }}
                                >
                                  Full
                                </Typography>
                              )}
                              {isBackup && (
                                <Typography
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    letterSpacing: 0.3,
                                    textTransform: 'uppercase',
                                    color: theme.color.grey.neutral300,
                                    border: `1px solid ${theme.color.grey.border}`,
                                    borderRadius: 3,
                                    padding: '0 4px',
                                  }}
                                >
                                  Backup
                                </Typography>
                              )}
                            </Box>
                            {timeLabel && (
                              <Typography style={{ fontSize: 11, color: theme.color.grey.neutral200 }}>
                                {timeLabel}
                              </Typography>
                            )}
                            {hasCapacityData && (
                              <Box display='flex' alignItems='center' style={{ gap: 8, marginTop: 4 }}>
                                <Box
                                  style={{
                                    flex: 1,
                                    height: 4,
                                    background: '#e8e8e8',
                                    borderRadius: 2,
                                    maxWidth: 110,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    style={{
                                      width: `${fillPercent}%`,
                                      height: '100%',
                                      borderRadius: 2,
                                      background: fillPercent >= 80 ? theme.palette.primary.main : '#4caf50',
                                    }}
                                  />
                                </Box>
                                <Typography
                                  style={{ fontSize: 11, color: '#666', fontWeight: 500, whiteSpace: 'nowrap' }}
                                >
                                  {session.currentCount}/{session.maxVolunteers} filled
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px', borderTop: `1px solid ${theme.color.grey.border}` }}>
        <Box display='flex' alignItems='center' style={{ marginRight: 'auto' }}>
          <Typography variant='body2' style={{ color: theme.color.grey.neutral200, fontWeight: 600 }}>
            {datesSelected} date{datesSelected !== 1 ? 's' : ''}, {totalSelected} session
            {totalSelected !== 1 ? 's' : ''}
            {sessionsShort > 0 ? ` (${sessionsShort} more needed)` : ''}
          </Typography>
        </Box>
        <TabletButton size='medium' onClick={onClose} style={{ textTransform: 'none', marginRight: 8 }}>
          Skip for now
        </TabletButton>
        <TabletButton
          size='medium'
          variant='contained'
          color='primary'
          disabled={totalSelected === 0 || !minimumMet || isLoading}
          onClick={handleConfirm}
          style={{ textTransform: 'none' }}
        >
          {isApplicant ? 'Confirm Selection' : hasOpenRoster ? 'Confirm & Roster' : 'Confirm Availability'}
        </TabletButton>
      </DialogActions>
    </FullscreenDialog>
  );
};
